import { ArrowRight, AlertTriangle, ShieldAlert, Info, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useAlarms } from "@/hooks/useAlarms";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { carryTo } from "@/lib/carryParams";
import { useT } from "@/lib/i18n";
import { formatRelativeAge } from "@/lib/format";
import { SEV_ORDER, severityBadgeClass } from "@/lib/severity";

const ICON_BY_SEV = {
  critical: ShieldAlert,
  major: AlertTriangle,
  minor: AlertCircle,
  warning: Info,
} as const;

// Status wire values stay the data; the visible badge word is translated
// at render. Unknown statuses fall through t() unchanged.
const STATUS_LABEL_KEY: Record<string, string> = {
  active: "monitor.alarmStatus.active",
  acknowledged: "monitor.alarmStatus.acknowledged",
  cleared: "monitor.alarmStatus.cleared",
};

export function AlarmsCompact({ limit = 4 }: { limit?: number }) {
  const t = useT();
  // Shared alarms cache (see useAlarms) — replaces a per-component 4 s poll.
  const { data: alarms = [] } = useAlarms();

  // Most severe first, newest within a severity; alarms the operator
  // deliberately snoozed don't nag on the landing page (ALM-07, OVW-01).
  const now = Date.now();
  const active = alarms
    .filter((a) => a.status !== "cleared" && !(a.snoozed_until && a.snoozed_until > now))
    .sort((a, b) => (SEV_ORDER[b.severity] - SEV_ORDER[a.severity]) || (b.raised_at - a.raised_at))
    .slice(0, limit);
  const activeCount = alarms.filter((a) => a.status === "active").length;
  const ackCount = alarms.filter((a) => a.status === "acknowledged").length;
  const hasCritical = alarms.some((a) => a.status === "active" && a.severity === "critical");

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center gap-2">
            {t("alarms.needsAttention")}
            <span className={cn(
              "rounded-full px-1.5 py-0.5 text-[10px] font-bold num",
              hasCritical ? "bg-critical/15 text-critical" :
              activeCount > 0 ? "bg-warn/15 text-warn" :
              "bg-surface-2 text-text-muted",
            )}>
              {ackCount > 0 ? t("monitor.alarmsCompact.activeAck", { n: activeCount, m: ackCount }) : activeCount}
            </span>
          </div>
        </CardTitle>
        <Link to={carryTo("/alarms")} className="flex items-center gap-1 text-xs text-accent hover:underline">
          {t("monitor.alarmsCompact.viewAll")} <ArrowRight size={11} />
        </Link>
      </CardHeader>
      <div className="divide-y divide-edge">
        {active.length === 0 && (
          <div className="px-4 py-6 text-center text-xs text-text-muted">{t("monitor.alarmsCompact.empty")}</div>
        )}
        {active.map((a) => {
          const Icon = ICON_BY_SEV[a.severity];
          // Meta line from truthy segments only — live alarms often carry
          // an EMPTY rule_type (the adapter omits unmappable ones), and
          // "Device ·  · raised…" reads broken.
          const meta = [a.device_name, a.rule_type, t("monitor.alarm.raised", { age: formatRelativeAge(now - a.raised_at) })]
            .filter((s) => s && s !== "unknown")
            .join(" · ");
          // Each mention links: lands on the console with this row
          // expanded, scrolled to and highlighted (?id= contract).
          return (
            <Link
              key={a.id}
              to={carryTo("/alarms", { id: a.id })}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3 transition hover:bg-surface-2/60"
            >
              <span className={cn("inline-grid h-7 w-7 place-items-center rounded-md border", severityBadgeClass(a.severity))}>
                <Icon size={14} />
              </span>
              <div>
                <div className="text-sm font-medium">{a.message}</div>
                <div className="text-[11px] text-text-muted">{meta}</div>
              </div>
              <span className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                a.status === "acknowledged" ? "bg-accent/15 text-accent" :
                a.status === "active" ? "bg-critical/15 text-critical" :
                "bg-ok/15 text-ok",
              )}>{t(STATUS_LABEL_KEY[a.status] ?? a.status)}</span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
